import {
  Suspense,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Outlet,
  Navigate,
  useParams,
  useMatch,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Spinner, EmptyState, PageHeader } from '@/components/ds';
import { PageCrumbs } from '@/components/layout/shell/PageCrumbs';
import type { Crumb } from '@/components/layout/shell/use-breadcrumbs';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { useAuth } from '@/contexts/AuthContext';
import {
  dashboardRoutePaths,
  sectionFromLegacyTab,
  settingsSectionPath,
  RENAMED_SETTINGS_SECTIONS,
  MOVED_SETTINGS_SECTIONS,
  type SettingsSection,
} from '@/routes/route-config';
import { findSectionSpec, canSeeSection, visibleSections } from './sections';
import { SettingsHub } from './SettingsHub';
import { ShieldOff } from 'lucide-react';

/**
 * The persistent frame around every settings section.
 *
 * It renders ONCE. Sections mount and unmount inside the `<Outlet />`, so
 * moving between them no longer tears down the header, the breadcrumbs, or the
 * sidebar — which is what the old 19-way `switch` inside a single component
 * did on every tab click.
 *
 * Everything shown here derives from the URL. No `activeTab` state exists to
 * fall out of sync with the address bar, so a deep link renders the right
 * section on first paint instead of flashing `profile` and then correcting.
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const { section: sectionParam } = useParams();
  const { search } = useLocation();

  // A detail route keeps its parent as the active section: the param is a
  // person, a role or a procedure, not a section of its own. All three have to
  // be resolved here. The role and procedure editors used to fall through with
  // no section at all, which left their breadcrumb reading just "Settings" and,
  // worse, skipped the permission check below, so the page shell rendered for
  // someone who may not open Roles or Procedures.
  const staffDetail = useMatch(
    `${dashboardRoutePaths.settings}/staff/:staffId`,
  );
  const roleDetail = useMatch(`${dashboardRoutePaths.settings}/roles/:roleId`);
  const procedureDetail = useMatch(
    `${dashboardRoutePaths.settings}/procedures/:procedureId`,
  );
  const detail: { parent: SettingsSection; leaf: string } | null = staffDetail
    ? { parent: 'staff', leaf: t('settings.staff.member', 'Staff member') }
    : roleDetail
      ? {
          parent: 'roles',
          leaf:
            roleDetail.params.roleId === 'new'
              ? t('settings.roles.createRole')
              : t('settings.roles.edit'),
        }
      : procedureDetail
        ? {
            parent: 'procedures',
            leaf:
              procedureDetail.params.procedureId === 'new'
                ? t('settings.procedures.addProcedure')
                : t('common.edit'),
          }
        : null;
  const activeId = detail ? detail.parent : sectionParam;

  /**
   * The primary action lives in the page header, but only the section knows
   * what it is and when it is permitted. The section registers it here rather
   * than rendering its own header card — which is what made every section
   * print its title and description a second time.
   */
  const [actions, setActions] = useState<ReactNode>(null);
  const setHeaderActions = useCallback(
    (node: ReactNode) => setActions(node ?? null),
    [],
  );
  const outletContext = useMemo<SettingsOutletContext>(
    () => ({ setHeaderActions }),
    [setHeaderActions],
  );

  // Plain lookups over a twenty-item array; nothing here is worth memoising.
  const spec = findSectionSpec(activeId);
  const allowed = spec ? canSeeSection(spec, permissions) : false;

  // A section that has been renamed keeps working: the old segment redirects
  // to the new one rather than being treated as unknown. Links to
  // /settings/clinics live in bookmarks and in audit-log rows against
  // historical records, none of which can be rewritten.
  const renamed = activeId ? RENAMED_SETTINGS_SECTIONS[activeId] : undefined;
  if (renamed) {
    return <Navigate to={settingsSectionPath(renamed)} replace />;
  }

  // A section that left settings for a page of its own: same target, same
  // filters in the query string.
  const moved = activeId ? MOVED_SETTINGS_SECTIONS[activeId] : undefined;
  if (moved) {
    return <Navigate to={moved + search} replace />;
  }

  // Unknown section in the URL — send the user somewhere real rather than
  // rendering an empty frame.
  if (activeId && !spec) {
    return <Navigate to={dashboardRoutePaths.settings} replace />;
  }

  // One trail, in the top bar. This layout used to print a second copy of it
  // directly under the bar, and the two disagreed: the bar derives its trail
  // from the sidebar's settings list, which has no Profile entry and no detail
  // routes, so it read "Settings" alone on Profile and stopped at the section
  // on a staff member or a role. Publishing the trail from here, where the
  // section is actually resolved, makes the bar right and the copy unnecessary.
  const crumbs: Crumb[] = spec
    ? [
        { label: t('nav.settings'), href: dashboardRoutePaths.settings },
        ...(detail
          ? [
              {
                label: t(spec.labelKey),
                href: settingsSectionPath(detail.parent),
              },
              { label: detail.leaf },
            ]
          : [{ label: t(spec.labelKey) }]),
      ]
    : [{ label: t('nav.settings') }];

  return (
    <DashboardLayout>
      <PageCrumbs crumbs={crumbs} />
      <div className="space-y-4 lg:space-y-6">
        {spec && !detail && (
          <PageHeader
            title={t(spec.labelKey)}
            description={t(spec.descriptionKey)}
            actions={actions}
          />
        )}
        {!activeId && <PageHeader title={t('nav.settings')} />}

        {spec && !allowed ? (
          <EmptyState
            icon={ShieldOff}
            title={t('common.noAccess', 'You do not have access to this')}
            description={t(
              'common.noAccessDescription',
              'Ask an administrator if you need access to this section.',
            )}
          />
        ) : (
          // A section that throws must not take the settings frame with it:
          // the section list stays on screen so the user can step sideways.
          <RouteErrorBoundary chrome="inline">
            <Suspense fallback={<SectionFallback />}>
              <Outlet context={outletContext} />
            </Suspense>
          </RouteErrorBoundary>
        )}
      </div>
    </DashboardLayout>
  );
}

/** Published from inside the layout, where the crumbs provider lives. */
export interface SettingsOutletContext {
  /**
   * Publish this section's header action. Pass `null` to clear it. Call it
   * from `useSettingsHeaderAction`, which handles the cleanup on unmount —
   * without that, a section's button survives into the next section.
   */
  setHeaderActions: (node: ReactNode) => void;
}

/**
 * Section-level loading. Deliberately NOT `<Lazy>`: that renders a full-screen
 * loader, which inside a shell that is already painted reads as the whole page
 * reloading on every section change.
 */
function SectionFallback() {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}

/**
 * `/dashboard/settings` itself is the settings overview, and it is also where
 * every legacy `?tab=` link lands.
 *
 * The query-param form was the app's only settings URL for its whole life, so
 * that shape sits in bookmarks, in sent emails, and in audit-log deep links
 * stored against historical records — none of which can be rewritten. It
 * redirects rather than renders, so the address bar ends up canonical and the
 * old URL is not re-shared from here. A legacy tab the user may not open is
 * treated as no tab at all, rather than bouncing them to a section that
 * immediately refuses them.
 */
export function SettingsIndex() {
  const { permissions } = useAuth();
  const [searchParams] = useSearchParams();

  const tab = searchParams.get('tab');
  const legacy = sectionFromLegacyTab(tab);
  const legacySpec = legacy
    ? visibleSections(permissions).find((s) => s.id === legacy)
    : undefined;

  const moved = tab ? MOVED_SETTINGS_SECTIONS[tab] : undefined;
  if (moved) return <Navigate to={moved} replace />;
  if (legacySpec) {
    return <Navigate to={settingsSectionPath(legacySpec.id)} replace />;
  }
  return <SettingsHub />;
}
